import { useCallback, useState } from 'react'
import { useMutation } from '@apollo/client'
import Button from '@mui/material/Button'
import { removeCrashCourse, removeCrashCourseVariables } from '__generatedTypes__/removeCrashCourse'
import { REMOVE_CRASH_COURSE } from 'queries/crashCourse'
import ConfirmationModal from 'components/Modals/ConfirmationModal'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { toast } from 'utils/toast'

export type RemoveButtonProps = {
  crashCourseId: string
  subjectId?: string
  submitting: boolean
  setSubmitting: (submitting: boolean) => void
  className?: string
}

export const RemoveButton = ({ crashCourseId, subjectId, submitting, setSubmitting, className }: RemoveButtonProps) => {
  const { navigate } = useLink()
  const [removeCrashCourse] = useMutation<removeCrashCourse, removeCrashCourseVariables>(REMOVE_CRASH_COURSE)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleRemove = useCallback(async () => {
    if (submitting) {
      return
    }

    setSubmitting(true)
    try {
      await removeCrashCourse({
        variables: {
          params: crashCourseId,
        },
      })

      toast.success('Crash course deleted successfully')

      navigate(
        cmsRoutes.CRASH_COURSE_LIST,
        undefined,
        subjectId ? { searchParams: new URLSearchParams({ subjectId }) } : undefined
      )
    } catch (e) {
      toast.error('Failed to delete crash course')
    }
    setIsModalOpen(false)
    setSubmitting(false)
  }, [crashCourseId, subjectId, submitting, navigate])

  return (
    <>
      <Button
        type="button"
        variant="outlined"
        color="red"
        size="large"
        disabled={submitting}
        onClick={() => setIsModalOpen(true)}
        className={className}
      >
        <TrashIcon /> Delete
      </Button>
      <ConfirmationModal
        open={isModalOpen}
        title="Deleting Crash Course"
        subtitle={
          <>
            Are you sure you want to delete this <br />
            crash course? This action cannot be changed.
          </>
        }
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleRemove}
      />
    </>
  )
}
